import type { UserPolicyInput, UserPolicyList } from '../../lib/user-policy'
import { TokenRefreshApiClient } from './token-refresh-client'
import type { SecureMessage } from './types'

type SendResponseFn = (messageId: string, data: unknown, targetOrigin: string) => void

interface UserPolicyMessageData {
  accessToken?: string
  userId?: string
  projectId?: string
  payload?: { policyId?: string } & Partial<UserPolicyInput>
}

function requirePolicyId(payload: UserPolicyMessageData['payload']): string {
  const policyId = payload?.policyId
  if (typeof policyId !== 'string' || policyId.length === 0) {
    throw new Error('policyId is required')
  }
  return policyId
}

function requireInput(payload: UserPolicyMessageData['payload']): UserPolicyInput {
  if (typeof payload?.name !== 'string' || !payload.rules) {
    throw new Error('name and rules are required')
  }
  return {
    name: payload.name,
    description: payload.description ?? '',
    enabled: payload.enabled ?? true,
    allChains: payload.allChains ?? false,
    chainIds: payload.chainIds ?? [],
    rules: payload.rules
  }
}

export class UserPolicyAPI extends TokenRefreshApiClient {
  private sendResponseFn: SendResponseFn

  constructor(sendResponseFn: SendResponseFn) {
    super()
    this.sendResponseFn = sendResponseFn
  }

  private path(userId: string, suffix = ''): string {
    return `/api/tss/${encodeURIComponent(userId)}/policies${suffix}`
  }

  async listUserPolicies(userId: string, projectId: string, accessToken: string): Promise<UserPolicyList> {
    return this.apiCall('GET', this.path(userId), undefined, projectId, accessToken)
  }

  async createUserPolicy(userId: string, projectId: string, input: UserPolicyInput, accessToken: string): Promise<UserPolicyList> {
    return this.apiCall('POST', this.path(userId), input, projectId, accessToken)
  }

  async updateUserPolicy(
    userId: string,
    projectId: string,
    policyId: string,
    input: UserPolicyInput,
    accessToken: string
  ): Promise<UserPolicyList> {
    return this.apiCall('PUT', this.path(userId, `/${encodeURIComponent(policyId)}`), input, projectId, accessToken)
  }

  async deleteUserPolicy(userId: string, projectId: string, policyId: string, accessToken: string): Promise<UserPolicyList> {
    return this.apiCall('POST', this.path(userId, `/${encodeURIComponent(policyId)}/delete`), {}, projectId, accessToken)
  }

  async cancelPending(userId: string, projectId: string, policyId: string, accessToken: string): Promise<UserPolicyList> {
    return this.apiCall('POST', this.path(userId, `/${encodeURIComponent(policyId)}/cancel-pending`), {}, projectId, accessToken)
  }

  async handleMessage(message: SecureMessage, origin: string): Promise<void> {
    const { type, messageId } = message
    const data = (message.data || {}) as UserPolicyMessageData
    const accessToken = data.accessToken
    const userId = data.userId
    const projectId = data.projectId

    if (!accessToken) throw new Error('accessToken is required')
    if (!userId) throw new Error('userId is required')
    if (!projectId) throw new Error('projectId is required')

    let view: UserPolicyList
    switch (type) {
      case 'USER_POLICY_LIST':
        view = await this.listUserPolicies(userId, projectId, accessToken)
        break
      case 'USER_POLICY_CREATE':
        view = await this.createUserPolicy(userId, projectId, requireInput(data.payload), accessToken)
        break
      case 'USER_POLICY_UPDATE':
        view = await this.updateUserPolicy(userId, projectId, requirePolicyId(data.payload), requireInput(data.payload), accessToken)
        break
      case 'USER_POLICY_DELETE':
        view = await this.deleteUserPolicy(userId, projectId, requirePolicyId(data.payload), accessToken)
        break
      case 'USER_POLICY_CANCEL_PENDING':
        view = await this.cancelPending(userId, projectId, requirePolicyId(data.payload), accessToken)
        break
      default:
        throw new Error(`[USER_POLICY_API]: Unknown message type: ${type}`)
    }

    this.sendResponseFn(messageId, view, origin)
  }
}
